import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { LogIn, Mail, Lock, AlertCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import TapTrackLogo from "../components/TapTrackLogo";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Engage — Sign in"; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      setError(null);
      toast.success("Welcome back!");
      const redirectPath = result.user.role === "fundraiser" ? "/dashboard" :
                          (result.user.role === "owner" || result.user.role === "manager") ? "/owner" :
                          result.user.role === "client" ? "/client" :
                          result.user.role === "charity_user" ? "/charity" : "/platform";
      navigate(redirectPath);
      return;
    }

    setError(result.error || "The email or password you entered didn't match our records. Please try again or use \"Forgot your password?\" to reset it. If the problem continues, contact your organisation administrator.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex flex-col">
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <Link to="/">
          <TapTrackLogo size="large" />
        </Link>
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-slate-200/80 shadow-xl shadow-slate-200/50 bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl font-black text-slate-900">
              Welcome to Engage
            </CardTitle>
            <CardDescription className="text-slate-400">
              Sign in to manage your operation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div 
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm"
                  data-testid="login-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 pl-10 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-14 pl-10 pr-12 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base rounded-xl transition-colors duration-150 active:scale-[0.98]"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-5 w-5" />
                    Sign In
                  </span>
                )}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-slate-500 hover:text-violet-600 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>

            <p className="mt-6 text-center text-slate-500">
              Don't have an account?{" "}
              <Link 
                to="/register" 
                className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
                data-testid="register-link"
              >
                Create account
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-sm text-slate-400">
        <span className="font-medium text-slate-500">Engage</span> — Powered by OCI · © 2026 OCI Systems Ltd
      </footer>
    </div>
  );
}
